// Stroke command — draws a stroke around the selection boundary or fills
// a stroke along a path. For MVP, stroke the selection boundary.

import type { Command } from "./Command";
import type { Document } from "../document/Document";
import type { BlendMode, RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { applyBlendMode } from "../pixel/brush";

export interface StrokeOptions {
  width: number;        // stroke width in pixels
  color: { r: number; g: number; b: number; a?: number };
  opacity: number;      // 0..1
  /** Where to place the stroke relative to the boundary. */
  position: "inside" | "center" | "outside";
  /**
   * Compositing mode for the stroke color (Photoshop's Stroke dialog →
   * Blending → Mode). Omitted/"normal" preserves the original plain
   * alpha-blend behavior exactly (#i186). Includes the painting-only modes
   * "clear" (erase) and "behind" that Photoshop's Stroke Mode dropdown offers
   * (both handled by applyBlendMode, which takes a plain string).
   */
  blendMode?: BlendMode | "clear" | "behind";
  /**
   * When true, the stroke only paints over pixels that already have some
   * opacity — mirrors Photoshop's Stroke dialog "Preserve Transparency"
   * checkbox. Defaults to false (#i186).
   */
  preserveTransparency?: boolean;
}

export class StrokeCommand implements Command {
  readonly kind = "stroke";
  private after?: Uint8ClampedArray;

  constructor(
    private readonly layerId: string,
    private readonly options: StrokeOptions,
    private readonly selectionMask?: Uint8Array,
    private before?: Uint8ClampedArray,
  ) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer || !this.selectionMask) return doc;

    const w = layer.pixels.width;
    const h = layer.pixels.height;
    const data = layer.pixels.data;

    // Capture the pre-stroke snapshot so undo can blit it back. strokePixel
    // mutates `data` in place (and 'clear'/'behind' modes reduce/branch on the
    // current pixel value), so re-running the stroke on undo would NOT revert —
    // it must restore the snapshot. (Same fix pattern as FillCommand.)
    if (!this.before) {
      this.before = new Uint8ClampedArray(data);
    }

    const { width, color, opacity, position } = this.options;
    const halfW = width / 2;
    const c = color;

    // Find selection boundary pixels and stroke around them
    for (let py = 0; py < h; py++) {
      for (let px = 0; px < w; px++) {
        const maskVal = this.selectionMask[py * w + px] ?? 0;

        // Only process edge pixels (where mask transitions)
        if (position === "inside" && maskVal === 0) continue;
        if (position === "outside" && maskVal > 0) continue;

        // For center: always draw; for inside/outside: only on boundary
        const isBoundary = maskVal > 0 && maskVal < 255;
        if (!isBoundary && position === "center") continue;

        // Draw stroke at this pixel and neighbors
        this.drawStroke(data, w, h, px, py, halfW, c, opacity);
      }
    }

    // If no boundary found (full selection or no selection), stroke the rect
    const bounds = this.findSelectionBounds(this.selectionMask, w, h);
    if (bounds) {
      const { x0, y0, x1, y1 } = bounds;

      // Stroke top and bottom edges
      for (let px = x0; px <= x1; px++) {
        for (let dy = 0; dy < width; dy++) {
          const ty = position === "outside" ? y0 - halfW + dy : y0 - halfW + dy;
          const by = position === "outside" ? y1 - halfW + dy : y1 - halfW + dy;
          if (position === "inside") {
            this.strokePixel(data, w, px, y0 + dy, c, opacity);
            this.strokePixel(data, w, px, y1 - dy, c, opacity);
          } else if (position === "outside") {
            this.strokePixel(data, w, px, y0 - halfW + dy, c, opacity);
            this.strokePixel(data, w, px, y1 + dy - halfW + 1, c, opacity);
          } else {
            this.strokePixel(data, w, px, y0 - halfW + dy, c, opacity);
            this.strokePixel(data, w, px, y1 - halfW + dy, c, opacity);
          }
        }
      }

      // Stroke left and right edges
      for (let py = y0; py <= y1; py++) {
        for (let dx = 0; dx < width; dx++) {
          if (position === "inside") {
            this.strokePixel(data, w, x0 + dx, py, c, opacity);
            this.strokePixel(data, w, x1 - dx, py, c, opacity);
          } else if (position === "outside") {
            this.strokePixel(data, w, x0 - halfW + dx, py, c, opacity);
            this.strokePixel(data, w, x1 + dx - halfW + 1, py, c, opacity);
          } else {
            this.strokePixel(data, w, x0 - halfW + dx, py, c, opacity);
            this.strokePixel(data, w, x1 - halfW + dx, py, c, opacity);
          }
        }
      }
    }

    layer.pixels.markDirty();
    this.after = new Uint8ClampedArray(data);
    return doc;
  }

  invert(_before: Document, _after: Document): Command {
    // Undo = blit the pre-stroke snapshot back (NOT re-run the stroke, which
    // would leave the stroke in place / double-erase for 'clear' mode).
    return this.makeRestore(this.before, this.after);
  }

  /** Restore command toggling between the pre- and post-stroke snapshots so
   *  undo↔redo never recompute the stroke. */
  private makeRestore(target: Uint8ClampedArray | undefined, opposite: Uint8ClampedArray | undefined): Command {
    const layerId = this.layerId;
    const self = this;
    return {
      kind: "stroke-restore",
      apply(doc: Document): Document {
        if (!target) return doc;
        const layer = findRaster(doc.layers, layerId);
        if (!layer) return doc;
        layer.pixels.data.set(target);
        layer.pixels.markDirty();
        return doc;
      },
      invert(): Command {
        return self.makeRestore(opposite, target);
      },
    };
  }

  private drawStroke(
    data: Uint8ClampedArray,
    w: number,
    h: number,
    cx: number,
    cy: number,
    radius: number,
    color: { r: number; g: number; b: number; a?: number },
    opacity: number,
  ): void {
    const r2 = radius * radius;
    const minX = Math.max(0, Math.floor(cx - radius));
    const minY = Math.max(0, Math.floor(cy - radius));
    const maxX = Math.min(w - 1, Math.ceil(cx + radius));
    const maxY = Math.min(h - 1, Math.ceil(cy + radius));

    for (let py = minY; py <= maxY; py++) {
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - cx;
        const dy = py + 0.5 - cy;
        if (dx * dx + dy * dy > r2) continue;
        this.strokePixel(data, w, px, py, color, opacity);
      }
    }
  }

  private strokePixel(
    data: Uint8ClampedArray,
    stride: number,
    px: number,
    py: number,
    color: { r: number; g: number; b: number; a?: number },
    opacity: number,
  ): void {
    if (px < 0 || py < 0) return;
    const h = data.length / 4 / stride;
    if (px >= stride || py >= h) return;

    const idx = (py * stride + px) * 4;
    const { blendMode, preserveTransparency } = this.options;
    const dstA0 = data[idx + 3]!;

    // Preserve Transparency (#i186): mirrors Photoshop's Stroke dialog
    // "Preserve Transparency" checkbox — restrict the stroke to pixels that
    // already have some opacity, leaving fully-transparent areas untouched
    // regardless of what the selection mask/boundary allows.
    if (preserveTransparency && dstA0 === 0) return;

    const srcA = (color.a ?? 255) / 255 * opacity;
    if (srcA <= 0) return;

    if (blendMode && blendMode !== "normal") {
      const dr = data[idx]! / 255;
      const dg = data[idx + 1]! / 255;
      const db = data[idx + 2]! / 255;
      const da = dstA0 / 255;
      const [outR, outG, outB, outA] = applyBlendMode(
        blendMode,
        color.r / 255, color.g / 255, color.b / 255, srcA,
        dr, dg, db, da,
      );
      data[idx] = Math.round(outR * 255);
      data[idx + 1] = Math.round(outG * 255);
      data[idx + 2] = Math.round(outB * 255);
      data[idx + 3] = Math.round(outA * 255);
      return;
    }

    // Default ("normal", the pre-#i186 behavior): blend RGB only — alpha is
    // intentionally left untouched, exactly as before, so existing callers
    // that never set blendMode see zero behavior change.
    data[idx] = Math.round(data[idx]! * (1 - srcA) + color.r * srcA);
    data[idx + 1] = Math.round(data[idx + 1]! * (1 - srcA) + color.g * srcA);
    data[idx + 2] = Math.round(data[idx + 2]! * (1 - srcA) + color.b * srcA);
  }

  private findSelectionBounds(mask: Uint8Array, w: number, h: number):
    { x0: number; y0: number; x1: number; y1: number } | null {
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let py = 0; py < h; py++) {
      for (let px = 0; px < w; px++) {
        if (mask[py * w + px]! > 0) {
          if (px < x0) x0 = px;
          if (py < y0) y0 = py;
          if (px > x1) x1 = px;
          if (py > y1) y1 = py;
        }
      }
    }
    if (x1 < 0) return null;
    return { x0, y0, x1, y1 };
  }
}
